import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { VolumeRunsGrid, type VolumeRunRow } from '../components/grids/VolumeRunsGrid';
import { AppHeader } from '../components/layout/AppHeader';
import { UserMenu } from '../components/layout/UserMenu';
import { BooksNav } from '../components/navigation/BooksNav';
import { VolumeRunSidePanel } from '../components/panels/VolumeRunSidePanel';
import { VolumeTiles, type VolumeProductFilter } from '../components/summary/VolumeTiles';
import { DEFAULT_LIST_PAGE_SIZE, PERIODS_API_URL, PRODUCTS_API_URL, SEARCH_API_URL, VOLUME_RUNS_API_URL } from '../constants';
import { paginateRows } from '../domain/contracts';
import { hasPermission, permissionsOf } from '../domain/formatters';
import type { LoginResponse } from '../domain/types';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

interface CommercialPageProps {
  auth: LoginResponse;
  onLogout: () => void | Promise<void>;
  authFetch: AuthFetch;
}

interface PeriodRow {
  id: string;
  periodId: string;
  status: string;
}

interface ContractPick {
  id: string;
  number: string;
  type: string;
  title: string;
  status: string;
}

interface ProductPick {
  code: string;
  name: string;
  unit: string;
  active: boolean;
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    throw new Error((await res.text()) || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

function tabClass(active: boolean): string {
  return active ? 'app-main-nav-link app-main-nav-link--active' : 'app-main-nav-link';
}

function Shell({
  auth,
  onLogout,
  detailFull,
  children
}: {
  auth: LoginResponse;
  onLogout: () => void | Promise<void>;
  detailFull?: boolean;
  children: ReactElement;
}): ReactElement {
  return (
    <div className={`page page-authed${detailFull ? ' page-authed--detail-full' : ''}`}>
      <AppHeader
        navSlot={<BooksNav />}
        endSlot={
          <UserMenu auth={auth} onLogout={onLogout} />
        }
      />
      <div className={`app-content${detailFull ? ' app-content--detail-full' : ''}`}>{children}</div>
    </div>
  );
}

export function CommercialPage(props: CommercialPageProps): ReactElement {
  const { auth, onLogout, authFetch } = props;
  const { runId } = useParams();
  const { pathname } = useLocation();
  const perms = permissionsOf(auth);
  const canRead = hasPermission(perms, 'doi:read', auth.role);
  const canWrite = hasPermission(perms, 'doi:update', auth.role);
  const isNew = pathname === '/commercial/new';
  const isDetail = Boolean(runId) && !isNew;

  if (!canRead) {
    return (
      <Shell auth={auth} onLogout={onLogout}>
        <p className="error">You do not have permission to view Commercial.</p>
      </Shell>
    );
  }

  if (isNew && !canWrite) {
    return <Navigate to="/commercial" replace />;
  }

  if (isDetail && runId) {
    return (
      <Shell auth={auth} onLogout={onLogout} detailFull>
        <VolumeDetailRoute authFetch={authFetch} id={runId} />
      </Shell>
    );
  }

  return (
    <Shell auth={auth} onLogout={onLogout}>
      <section>
        <h2>Commercial</h2>
        <p>Record well-month volume and allocate it on the selected contract DOI. Typed entry in v1 — Merrick is not assumed.</p>
        <nav className="app-main-nav" aria-label="Commercial register">
          <Link to="/commercial" className={tabClass(!isNew)} data-testid="commercial-tab-search">
            Search
          </Link>
          {canWrite && (
            <Link to="/commercial/new" className={tabClass(isNew)} data-testid="volume-new-tab">
              New volume
            </Link>
          )}
        </nav>
        {isNew ? (
          <VolumeForm authFetch={authFetch} />
        ) : (
          <VolumeRegister authFetch={authFetch} canWrite={canWrite} />
        )}
      </section>
    </Shell>
  );
}

function VolumeDetailRoute({ authFetch, id }: { authFetch: AuthFetch; id: string }): ReactElement {
  const navigate = useNavigate();
  const [item, setItem] = useState<VolumeRunRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const rows = await readJson<VolumeRunRow[]>(await authFetch(VOLUME_RUNS_API_URL));
    setItem(rows.find((r) => r.id === id) ?? null);
    setLoading(false);
  }, [authFetch, id]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      {error && <p className="error">{error}</p>}
      <VolumeRunSidePanel
        item={item}
        loading={loading}
        variant="page"
        busy={busy}
        onClose={() => navigate('/commercial')}
        onBack={() => navigate('/commercial')}
        onResolve={async (runId, gravity) => {
          setBusy(true);
          setError('');
          try {
            await readJson(
              await authFetch(`${VOLUME_RUNS_API_URL}/${runId}/resolve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ gravity: gravity ?? null })
              })
            );
            await load();
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Resolve failed');
          } finally {
            setBusy(false);
          }
        }}
        onRebook={async (runId, productionDate) => {
          setBusy(true);
          setError('');
          try {
            const created = await readJson<VolumeRunRow>(
              await authFetch(`${VOLUME_RUNS_API_URL}/${runId}/rebook`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productionDate })
              })
            );
            navigate(`/commercial/${created.id}`);
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Reverse/rebook failed');
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

function VolumeRegister({ authFetch, canWrite }: { authFetch: AuthFetch; canWrite: boolean }): ReactElement {
  const navigate = useNavigate();
  const [runs, setRuns] = useState<VolumeRunRow[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<VolumeProductFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRuns(await readJson<VolumeRunRow[]>(await authFetch(VOLUME_RUNS_API_URL)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load volume runs');
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') {
      return runs;
    }
    return runs.filter((r) => r.product.toLowerCase() === filter);
  }, [filter, runs]);

  const counts = useMemo(
    () => ({
      all: runs.length,
      oil: runs.filter((r) => r.product.toLowerCase() === 'oil').length,
      gas: runs.filter((r) => r.product.toLowerCase() === 'gas').length
    }),
    [runs]
  );

  const pageRows = useMemo(() => paginateRows(filtered, page, pageSize), [filtered, page, pageSize]);
  const selected = runs.find((r) => r.id === selectedId) ?? null;

  return (
    <>
      <VolumeTiles
        counts={counts}
        filter={filter}
        onFilter={(next) => {
          setFilter(next);
          setPage(1);
        }}
      />
      {error && <p className="error">{error}</p>}
      <div className={`requests-shell${selectedId ? ' has-panel' : ''}`}>
        <VolumeRunsGrid
          rows={pageRows}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onOpen={(id) => navigate(`/commercial/${id}`)}
          onNew={canWrite ? () => navigate('/commercial/new') : undefined}
          loading={loading}
          page={page}
          pageSize={pageSize}
          totalCount={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
        {selectedId && (
          <VolumeRunSidePanel
            item={selected}
            variant="panel"
            onClose={() => setSelectedId(null)}
            onOpenFullPage={(id) => navigate(`/commercial/${id}`)}
          />
        )}
      </div>
    </>
  );
}

function VolumeForm({ authFetch }: { authFetch: AuthFetch }): ReactElement {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [contracts, setContracts] = useState<ContractPick[]>([]);
  const [wellId, setWellId] = useState('');
  const [wellLabel, setWellLabel] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [product, setProduct] = useState('OIL');
  const [products, setProducts] = useState<ProductPick[]>([]);
  const [gross, setGross] = useState('1000');
  const [productionDate, setProductionDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [contractId, setContractId] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const periodRows = await readJson<PeriodRow[]>(await authFetch(PERIODS_API_URL));
        setPeriods(periodRows);
        const open = periodRows.find((p) => p.status === 'Open') ?? periodRows[0];
        if (open) {
          setPeriodId(open.periodId);
        }
        const catalog = await readJson<{ items?: ProductPick[] }>(
          await authFetch(`${PRODUCTS_API_URL}?active=true`)
        );
        const active = (catalog.items ?? []).filter((p) => p.active !== false);
        setProducts(active);
        if (active.some((p) => p.code === 'OIL')) {
          setProduct('OIL');
        } else if (active[0]) {
          setProduct(active[0].code);
        }
        const data = await readJson<{ wells: { id: string; label: string }[] }>(
          await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent('42123400010000')}`)
        );
        const hit = data.wells[0];
        if (!hit) {
          return;
        }
        setWellId(hit.id);
        setWellLabel(hit.label);
        const picks = await readJson<ContractPick[]>(
          await authFetch(`${VOLUME_RUNS_API_URL}/contracts-for-well/${hit.id}`)
        );
        setContracts(picks);
        const lease =
          picks.find((c) => c.number === 'T-01') ??
          picks.find((c) => c.type === 'OilGasLease') ??
          picks[0];
        if (lease) {
          setContractId(lease.id);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load volume form');
      }
    })();
  }, [authFetch]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const created = await readJson<VolumeRunRow>(
        await authFetch(VOLUME_RUNS_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            periodId,
            wellId,
            contractId: contractId || undefined,
            product,
            productionDate,
            gross: Number(gross)
          })
        })
      );
      navigate(`/commercial/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Volume post failed');
    }
  };

  return (
    <form className="grid" onSubmit={onSubmit} data-testid="volume-run-form">
      {error && <p className="error">{error}</p>}
      <label>
        Period
        <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} data-testid="volume-period">
          {periods.map((p) => (
            <option key={p.id} value={p.periodId}>
              {p.periodId} ({p.status})
            </option>
          ))}
        </select>
      </label>
      <label>
        Well
        <input value={wellLabel} readOnly data-testid="volume-well" />
      </label>
      <label>
        Contract
        <select value={contractId} onChange={(e) => setContractId(e.target.value)} data-testid="volume-contract">
          <option value="">Select contract</option>
          {contracts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.number} — {c.title}
            </option>
          ))}
        </select>
      </label>
      <label>
        Product
        <select value={product} onChange={(e) => setProduct(e.target.value)} data-testid="volume-product">
          {products.map((p) => (
            <option key={p.code} value={p.code}>
              {p.code} ({p.unit})
            </option>
          ))}
        </select>
      </label>
      <label>
        Production date
        <input type="date" value={productionDate} onChange={(e) => setProductionDate(e.target.value)} />
      </label>
      <label>
        Gross
        <input value={gross} onChange={(e) => setGross(e.target.value)} data-testid="volume-gross" />
      </label>
      <div className="row">
        <button type="submit" data-testid="volume-save">
          Post volume run
        </button>
        <button type="button" className="secondary" onClick={() => navigate('/commercial')}>
          Cancel
        </button>
      </div>
    </form>
  );
}
